import { EnumTypeValue } from "@/shared/types";
import { VsmStatus } from "../constants";
import { Maatregel, RijdtMaatregel } from "./maatregel";

export const VergelijkingStatus = {
    NIEUW: "Nieuw",
    GEWIJZIGD: "Gewijzigd",
    VERVALLEN: "Vervallen",
    ONGEWIJZIGD: "Ongewijzigd",
} as const;

export type VergelijkingStatus = EnumTypeValue<typeof VergelijkingStatus>;

// The velden of a maatregel that are compared between huidige and nieuwe versie.
export type MaatregelVeld = "type" | "vanStationCode" | "naarStationCode" | "richting" | "vertrekTijd" | "opmerking" | "omleiding";

export type MaatregelVergelijking = {
    status: VergelijkingStatus;
    gewijzigdeVelden: MaatregelVeld[];
};

export type VsmVergelijking = {
    nieuw: Map<string, MaatregelVergelijking>;
    huidig: Map<string, MaatregelVergelijking>;
};

const UNCHANGED = { status: "Ongewijzigd", gewijzigdeVelden: [] } satisfies MaatregelVergelijking;

const flattenMaatregelen = (maatregelen: Maatregel[]): Maatregel[] => {
    const flattened = maatregelen.flatMap((maatregel) => (maatregel.type === "RijdtGroep" ? maatregel.maatregelen : [maatregel]));
    // Members of a RijdtGroep also appear on their own, so without this they would be compared twice.
    return [...new Map(flattened.map((maatregel) => [maatregel.id, maatregel])).values()];
};

// Only accepted toelichting text belongs to the maatregel. Suggestions do not affect pairing or change markers.
const acceptedToelichting = (maatregel: Maatregel): string =>
    maatregel.opmerkingIsSpoorwebSuggestie ? "" : (maatregel.opmerking?.trim() ?? "");

const viaStations = (maatregel: Maatregel): string => (maatregel.type === "Omleiding" ? maatregel.omleiding.viaStationCodes.join(",") : "");

const findChangedVelden = (nieuw: Maatregel, huidig: Maatregel): MaatregelVeld[] => {
    const velden: MaatregelVeld[] = [];

    if ((nieuw.type ?? "") !== (huidig.type ?? "")) {
        velden.push("type");
    }
    if ((nieuw.vanStationCode ?? "") !== (huidig.vanStationCode ?? "")) {
        velden.push("vanStationCode");
    }
    if ((nieuw.naarStationCode ?? "") !== (huidig.naarStationCode ?? "")) {
        velden.push("naarStationCode");
    }
    if (nieuw.richting !== huidig.richting) {
        velden.push("richting");
    }
    if ((nieuw.vertrekTijd ?? "") !== (huidig.vertrekTijd ?? "")) {
        velden.push("vertrekTijd");
    }
    if (viaStations(nieuw) !== viaStations(huidig)) {
        velden.push("omleiding");
    }
    if (acceptedToelichting(nieuw) !== acceptedToelichting(huidig)) {
        velden.push("opmerking");
    }

    return velden;
};

// Maatregelen are paired within a section, so a treinserie that moved to another section is nieuw + vervallen.
const maatregelKey = (maatregel: Maatregel): string => `${Maatregel.getSection(maatregel)}|${maatregel.treinserie}`;

const groupByMaatregelKey = (maatregelen: Maatregel[]): Map<string, Maatregel[]> => {
    const groups = new Map<string, Maatregel[]>();
    for (const maatregel of maatregelen) {
        const key = maatregelKey(maatregel);
        groups.set(key, [...(groups.get(key) ?? []), maatregel]);
    }
    return groups;
};

type MaatregelPair = { nieuw: Maatregel; huidig: Maatregel; velden: MaatregelVeld[] };

// Reserve exact pairs first, then pairs that differ only in toelichting, before falling back to list order.
const pairMaatregelen = (nieuweMaatregelen: Maatregel[], huidigeMaatregelen: Maatregel[]) => {
    const pairs: MaatregelPair[] = [];
    const unpairedNieuw = [...nieuweMaatregelen];
    const unpairedHuidig = [...huidigeMaatregelen];

    const pairMatching = (matches: (velden: MaatregelVeld[]) => boolean) => {
        for (const nieuw of [...unpairedNieuw]) {
            const index = unpairedHuidig.findIndex((huidig) => matches(findChangedVelden(nieuw, huidig)));
            if (index === -1) {
                continue;
            }

            const huidig = unpairedHuidig[index];
            pairs.push({ nieuw, huidig, velden: findChangedVelden(nieuw, huidig) });
            unpairedHuidig.splice(index, 1);
            unpairedNieuw.splice(unpairedNieuw.indexOf(nieuw), 1);
        }
    };

    pairMatching((velden) => velden.length === 0);
    pairMatching((velden) => velden.every((veld) => veld === "opmerking"));

    while (unpairedNieuw.length > 0 && unpairedHuidig.length > 0) {
        const nieuw = unpairedNieuw.shift()!;
        const huidig = unpairedHuidig.shift()!;
        pairs.push({ nieuw, huidig, velden: findChangedVelden(nieuw, huidig) });
    }

    return { pairs, unpairedNieuw, unpairedHuidig };
};

export const VsmVergelijking = {
    empty: (): VsmVergelijking => ({ nieuw: new Map(), huidig: new Map() }),

    unchanged: (): MaatregelVergelijking => UNCHANGED,

    /**
     * Compare both versies per section. Always recomputed from the full lists, never incrementally.
     *
     * De huidige versie is the reference the user compares against, so it only gets a verdict when a
     * treinserie disappears. Nieuw and Gewijzigd, and the marked velden, belong to de nieuwe versie.
     */
    compare: (nieuweMaatregelen: Maatregel[], huidigeMaatregelen: Maatregel[]): VsmVergelijking => {
        const nieuw = new Map<string, MaatregelVergelijking>();
        const huidig = new Map<string, MaatregelVergelijking>();

        const nieuweByKey = groupByMaatregelKey(flattenMaatregelen(nieuweMaatregelen));
        const huidigeByKey = groupByMaatregelKey(flattenMaatregelen(huidigeMaatregelen));

        for (const key of new Set([...nieuweByKey.keys(), ...huidigeByKey.keys()])) {
            const { pairs, unpairedNieuw, unpairedHuidig } = pairMaatregelen(nieuweByKey.get(key) ?? [], huidigeByKey.get(key) ?? []);

            for (const pair of pairs) {
                if (pair.velden.length > 0) {
                    nieuw.set(pair.nieuw.id, { status: "Gewijzigd", gewijzigdeVelden: pair.velden });
                }
            }
            for (const maatregel of unpairedNieuw) {
                nieuw.set(maatregel.id, { status: "Nieuw", gewijzigdeVelden: [] });
            }
            for (const maatregel of unpairedHuidig) {
                huidig.set(maatregel.id, { status: "Vervallen", gewijzigdeVelden: [] });
            }
        }

        return { nieuw, huidig };
    },

    // A RijdtGroep has no verdict of its own, it derives one from its members.
    forMaatregel: (verdicts: Map<string, MaatregelVergelijking>, maatregel: Maatregel): MaatregelVergelijking => {
        if (maatregel.type === "RijdtGroep") {
            const isGewijzigd = maatregel.maatregelen.some((m) => verdicts.get(m.id)?.status === "Gewijzigd");
            return isGewijzigd ? { status: "Gewijzigd", gewijzigdeVelden: [] } : UNCHANGED;
        }
        return verdicts.get(maatregel.id) ?? UNCHANGED;
    },

    hasTreinserieInSameSection: (maatregelen: Maatregel[], maatregel: Maatregel): boolean =>
        flattenMaatregelen(maatregelen).some((andere) => maatregelKey(andere) === maatregelKey(maatregel)),

    // Nieuwe en vervallen treinseries get their own row, so they can carry their tag and copy-button.
    belongsInRijdtGroep:
        (andereVersie: Maatregel[]) =>
        (maatregel: Maatregel): maatregel is RijdtMaatregel =>
            Maatregel.rijdtMaarZonderExtraInfo(maatregel) && VsmVergelijking.hasTreinserieInSameSection(andereVersie, maatregel),

    isStatusGewijzigd: (nieuweStatus: VsmStatus, huidigeStatus: VsmStatus): boolean => nieuweStatus !== huidigeStatus,
};
